import { AddCategoryTemplate } from "portal/components/categoryTemplates/createCategory/AddCategoryTemplate";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { CategoryTemplatesOverviewDialog } from "portal/components/categoryCollectionProfile/instructions/CategoryTemplatesOverviewDialog";
import {
  CursorFetchArgs,
  CursorPageResponse,
  useCursorDataSource,
} from "portal/utils/hooks/useCursorDataSource";
import { CustomerScopeSelector } from "portal/components/customers/CustomerScopeSelector";
import { DateTime } from "luxon";
import { DEBOUNCE_MS } from "portal/utils/timing";
import { FeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import { getCategoryTemplatesPath } from "portal/utils/routing";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Header } from "portal/components/header/Header";
import {
  isIncompleteRosySearch,
  ROSY_SEARCH_MIN_LENGTH,
  toRosySearchQuery,
} from "portal/utils/categoryCollection/search";
import { Link } from "@mui/material";
import { NoScroll, Page } from "portal/components/Page";
import { portalEndpoints } from "portal/state/portalApi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { SavedLibraryCategory } from "protos/portal/category_profile";
import { ScopedCustomerMissingFeatureFlagAlert } from "portal/components/customers/ScopedCustomerMissingFeatureFlagAlert";
import { SearchField } from "portal/components/header/SearchField";
import { useCanUpdateCategoryTemplatesPage } from "portal/views/categoryTemplates/permissions";
import { useCustomerScopeFilter } from "portal/components/customers/useCustomerScopeFilter";
import { useDebounce } from "@uidotdev/usehooks";
import { useLazyPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { VersionUser } from "portal/components/categoryCollectionProfile/history/VersionUser";
import CarbonLogo from "portal/images/logo_icon.svg?react";
import CategoryTemplatesIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from "react";

const defaultColumn: Partial<GridColDef<SavedLibraryCategory>> = {
  disableColumnMenu: true,
  flex: 1,
};

const _CategoryTemplatesList: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const canUpdate = useCanUpdateCategoryTemplatesPage();
  const { customerUuid } = useCustomerScopeFilter();

  const [searchInput, setSearchInput] = useState<string>("");
  const searchQuery = useDebounce(toRosySearchQuery(searchInput), DEBOUNCE_MS);
  const [listCategories] = useLazyPopups(
    portalEndpoints.listCategories.useLazyQuery()
  );
  const fetchCategoriesPage = useCallback(
    async ({
      cursor,
      limit,
    }: CursorFetchArgs): Promise<CursorPageResponse<SavedLibraryCategory>> => {
      const { data } = await listCategories({
        olderCursor: cursor,
        limit,
        search: searchQuery,
        inLibrary: true,
        customerUuid,
      });
      const categories = data?.categories ?? [];
      return {
        rows: categories,
        pageInfo: {
          hasNextPage: Boolean(data?.pagination?.nextOld),
          nextCursor: data?.pagination?.nextOld,
        },
      };
    },
    [customerUuid, listCategories, searchQuery]
  );
  const { dataGridProps } =
    useCursorDataSource<SavedLibraryCategory>(fetchCategoriesPage);

  const columns: readonly GridColDef<SavedLibraryCategory>[] = useMemo(
    () => [
      {
        ...defaultColumn,
        field: "name",
        headerName: t("models.categoryCollectionProfiles.fields.name"),
        valueGetter: (_value, { category }) => category?.name ?? "",
        cellClassName: "flex",
        renderCell: ({ row: { category } }) =>
          category?.id ? (
            <Link
              component={RouterLink}
              to={getCategoryTemplatesPath(category.id)}
              underline="hover"
              color="inherit"
              className="flex items-center gap-1.5"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <CategoryTemplatesIcon
                fontSize="small"
                aria-label={t("views.categoryTemplates.title")}
              />
              {category.carbon && (
                <CarbonLogo className="h-3 w-3 shrink-0 text-carbon-orange" />
              )}
              {category.name}
            </Link>
          ) : undefined,
      },
      {
        ...defaultColumn,
        flex: 0.5,
        field: "imageCount",
        headerName: t("views.categoryTemplates.fields.images"),
        type: "number",
        align: "center",
        headerAlign: "center",
        valueGetter: (_value, { category }) => category?.chipIds.length ?? 0,
      },
      {
        ...defaultColumn,
        flex: 0.75,
        field: "updatedBy",
        headerName: t("views.categoryTemplates.fields.updatedBy"),
        cellClassName: "flex",
        renderCell: ({ row: { metadata } }) =>
          metadata ? <VersionUser user={metadata.user} /> : undefined,
      },
      {
        ...defaultColumn,
        flex: 0.75,
        field: "clientVersionCreateTime",
        headerName: t("models.categoryCollectionProfiles.fields.updatedAt"),
        valueFormatter: (_value, { metadata }) =>
          metadata?.clientVersionCreateTime
            ? DateTime.fromJSDate(
                new Date(metadata.clientVersionCreateTime)
              ).toLocaleString(
                { ...DateTime.TIME_SIMPLE, ...DateTime.DATE_FULL },
                { locale: i18n.language }
              )
            : undefined,
      },
      {
        ...defaultColumn,
        flex: 0.75,
        field: "addedBy",
        headerName: t("views.categoryTemplates.fields.addedBy"),
        cellClassName: "flex",
        renderCell: ({ row: { libraryMetadata } }) =>
          libraryMetadata ? (
            <VersionUser user={libraryMetadata.addedBy} />
          ) : undefined,
      },
    ],
    [t, i18n.language]
  );

  return (
    <>
      <Header title={t("views.categoryTemplates.title")}>
        <CustomerScopeSelector />
      </Header>
      <Page>
        <NoScroll>
          <ScopedCustomerMissingFeatureFlagAlert
            flag={FeatureFlag.CUSTOM_CATEGORIES}
          />
          <CarbonDataGrid<SavedLibraryCategory>
            {...dataGridProps}
            key={customerUuid ?? ""}
            header={
              <div className="flex w-full items-center gap-4">
                <SearchField
                  value={searchInput}
                  onChange={setSearchInput}
                  helperText={
                    isIncompleteRosySearch(searchInput)
                      ? t("components.header.search.minLength", {
                          count: ROSY_SEARCH_MIN_LENGTH,
                        })
                      : undefined
                  }
                  label={t("utils.actions.searchLong", {
                    subject: t(
                      "models.categoryCollectionProfiles.category_other",
                      {
                        count: 2,
                      }
                    ),
                  })}
                  className="min-w-40 flex-1 md:max-w-md"
                />
                <CategoryTemplatesOverviewDialog />
                {canUpdate && (
                  <AddCategoryTemplate customerUuid={customerUuid} />
                )}
              </div>
            }
            className="flex flex-1"
            columns={columns}
            getRowId={({ category }) => category?.id ?? ""}
            onRowClick={({ row: { category } }) => {
              if (category?.id) {
                navigate(getCategoryTemplatesPath(category.id));
              }
            }}
            disableRowSelectionOnClick
          />
        </NoScroll>
      </Page>
    </>
  );
};

export const CategoryTemplatesList = _CategoryTemplatesList;
